import {
  createSlice,
  createAsyncThunk,
  createSelector,
}                       from "@reduxjs/toolkit"
import api              from "../api/axiosInstance"

// ── Thunk ─────────────────────────────────────────────────────
export const fetchMenu = createAsyncThunk(
  "menu/fetchMenu",
  async (emp_id, { rejectWithValue }) => {
    const t0 = Date.now()
    console.log("🚀 fetchMenu START for:", emp_id)
    try {
      const { data } = await api.post(
        "/api/menu/getMenuForUser",
        { emp_id },
        { timeout: 300000 }
      )
      console.log(`✅ fetchMenu: ${data.length} rows in ${Date.now()-t0}ms`)
      if (!data || !Array.isArray(data)) {
        return rejectWithValue("Invalid menu response")
      }
      return data
    } catch (err) {
      console.error("❌ fetchMenu failed:", err.message)
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed"
      )
    }
  }
)

// ── buildMenuTree — outside Redux/Immer ──────────────────────
// NEVER call this inside a reducer — Immer silently reverts
// complex nested objects causing state to stay at initialState
const buildMenuTree = (rows) => {
  if (!rows?.length) return []
  try {
    const sorted = [...rows].sort((a, b) => {
      const l = (Number(a.MENU_LEVEL)||0) - (Number(b.MENU_LEVEL)||0)
      return l !== 0 ? l : (Number(a.MENU_ORDER)||0) - (Number(b.MENU_ORDER)||0)
    })

    const childrenOf = {}
    sorted.forEach(r => {
      const pid = r.PARENT_MENU_ID
      if (pid != null && pid !== 0 && pid !== "0" && pid !== "") {
        const key = Number(pid)
        if (!childrenOf[key]) childrenOf[key] = []
        childrenOf[key].push(r)
      }
    })

    const allIds = new Set(sorted.map(r => Number(r.MENU_ID)))

    const roots = sorted.filter(r => {
      const pid = r.PARENT_MENU_ID
      if (pid == null || pid === 0 || pid === "0" || pid === "") return true
      return !allIds.has(Number(pid))
    })

    const toNode = (r) => {
      const kids = (childrenOf[Number(r.MENU_ID)] || []).map(toNode)
      return {
        id:         Number(r.MENU_ID),
        label:      String(r.MENU_NAME   || ""),
        url:        r.MENU_URL           || null,
        icon:       (r.MENU_ICON         || "default").toLowerCase(),
        moduleCode: String(r.MODULE_CODE || ""),
        menuLevel:  Number(r.MENU_LEVEL) || 1,
        menuOrder:  Number(r.MENU_ORDER) || 0,
        formKey:    r.FORM_KEY           || null,
        leaf:       kids.length === 0 && !!r.MENU_URL,
        permissions: {
          canView:   r.CAN_VIEW   === "Y",
          canAdd:    r.CAN_ADD    === "Y",
          canEdit:   r.CAN_EDIT   === "Y",
          canDelete: r.CAN_DELETE === "Y",
          canPrint:  r.CAN_PRINT  === "Y",
          canExport: r.CAN_EXPORT === "Y",
        },
        subs: kids,
      }
    }

    const tree = roots.map(toNode)
    console.log(`🌳 buildMenuTree: ${tree.length} roots from ${sorted.length} rows`)
    return tree

  } catch (err) {
    console.error("❌ buildMenuTree error:", err)
    return []
  }
}

// ── Slice ─────────────────────────────────────────────────────
const menuSlice = createSlice({
  name: "menu",
  initialState: {
    rawRows: [],      // plain flat array — safe for Immer
    status:  "idle",
    error:   null,
    // NO tree in state — built in selector to avoid Immer issues
  },
  reducers: {
    clearMenu: (state) => {
      state.rawRows = []
      state.status  = "idle"
      state.error   = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenu.pending, (state) => {
        console.log("🎯 REDUCER pending")
        state.status  = "loading"
        state.error   = null
        state.rawRows = []
      })
      .addCase(fetchMenu.fulfilled, (state, action) => {
        console.log("🎯 REDUCER fulfilled:", action.payload?.length, "rows")
        state.status  = "succeeded"
        state.rawRows = action.payload  // plain flat array only
        state.error   = null
        // ❌ DO NOT add: state.tree = buildMenuTree(...)
        // Immer throws silently on nested objects → state reverts
      })
      .addCase(fetchMenu.rejected, (state, action) => {
        console.log("🎯 REDUCER rejected:", action.payload)
        state.status  = "failed"
        state.error   = action.payload || "Failed"
        state.rawRows = []
      })
  },
})

export const { clearMenu } = menuSlice.actions

// ── Selectors — all memoized ──────────────────────────────────
const _sel = (s) => s.menu

export const selectMenuRawRows = createSelector(
  _sel,
  (m) => m?.rawRows || []
)

// Tree computed here in selector — safe, outside Immer
export const selectMenuTree = createSelector(
  selectMenuRawRows,
  (rawRows) => buildMenuTree(rawRows)
)

export const selectMenuStatus = (s) => s.menu?.status || "idle"
export const selectMenuError  = (s) => s.menu?.error  || null

export const selectSearchableMenuItems = createSelector(
  selectMenuRawRows,
  (rows) => rows.filter(r =>
    r.FORM_KEY && r.FORM_KEY !== "" &&
    r.MENU_URL && r.MENU_URL !== ""
  )
)

export const selectFormPermissions = (formKey) => createSelector(
  selectMenuRawRows,
  (rows) => {
    const row = rows.find(r => r.FORM_KEY === formKey)
    if (!row) return {
      canView:false, canAdd:false, canEdit:false,
      canDelete:false, canPrint:false, canExport:false,
    }
    return {
      canView:   row.CAN_VIEW   === "Y",
      canAdd:    row.CAN_ADD    === "Y",
      canEdit:   row.CAN_EDIT   === "Y",
      canDelete: row.CAN_DELETE === "Y",
      canPrint:  row.CAN_PRINT  === "Y",
      canExport: row.CAN_EXPORT === "Y",
    }
  }
)

export default menuSlice.reducer